<template>
    <PageWrapper :loading="isLoading">
        <template #header>
            <!-- Save and Cancel sit at the bottom of the header, next to the settings they apply to. -->
            <div class="PageWrapper__header GoAppSettings__header">
                <div class="GoAppSettings__headerText">
                    <div class="PageWrapper__headerTitle GoAppSettings__title">
                        Evident Go
                    </div>
                    <div class="PageWrapper__headerDescription GoAppSettings__description">
                        Control how employees find and add entities in Evident Go.
                    </div>
                </div>
                <div v-if="goAppEnabled && !loadFailed" class="GoAppSettings__actions">
                    <NewButton
                        variant="outline"
                        color="neutral"
                        :disabled="!isDirty || saving"
                        @click="cancel"
                    >
                        Cancel
                    </NewButton>
                    <span v-tooltip="saveBlockedReason">
                        <NewButton
                            variant="fill"
                            color="primary"
                            :loading="saving"
                            :disabled="!isDirty || Boolean(saveBlockedReason)"
                            @click="save"
                        >
                            Save
                        </NewButton>
                    </span>
                </div>
            </div>
        </template>
        <UnsavedChangesModal
            v-if="pendingNavigation"
            @input="onUnsavedChoice"
        />
        <div v-if="!goAppEnabled" class="GoAppSettings__disabledNotice">
            Go App is not enabled for this client. Contact Evident support to enable it.
        </div>
        <div v-else-if="loadFailed" class="GoAppSettings__loadError">
            Go App settings could not be loaded. Try again later.
        </div>
        <div v-else class="GoAppSettings__form">
            <section class="GoAppSettings__card">
                <div class="GoAppSettings__section">
                    <div class="GoAppSettings__sectionIntro">
                        <h2 class="GoAppSettings__sectionTitle">
                            Lookup property
                        </h2>
                        <p class="GoAppSettings__sectionDescription">
                            What employees type into search to find an existing entity.
                        </p>
                    </div>
                    <div class="GoAppSettings__sectionBody">
                        <div
                            class="GoAppSettings__choice"
                            :class="{ 'GoAppSettings__choice--selected': form.mode === 'custom' }"
                            data-test-id="GoAppSettings--customMode"
                        >
                            <RadioOption
                                class="GoAppSettings__choiceHeader"
                                name="goAppLookupMode"
                                :checked="form.mode === 'custom'"
                                :disabled="saving"
                                @input="form.mode = 'custom'"
                            >
                                <span class="GoAppSettings__choiceTitle">Custom property</span>
                                <span class="GoAppSettings__choiceDescription">
                                    Search by one identifier, such as a reference ID.
                                </span>
                            </RadioOption>
                            <div v-if="form.mode === 'custom'" class="GoAppSettings__choiceBody">
                                <div class="GoAppSettings__row">
                                    <EnumInput
                                        id="goAppSettingsLookupField"
                                        :disabled="saving"
                                        label="Property"
                                        required
                                        :value="form.lookupFieldKey || ''"
                                        :options="lookupFieldOptions"
                                        @input="onLookupFieldChange"
                                    />
                                    <TextInput
                                        id="goAppSettingsLookupRegex"
                                        :disabled="saving"
                                        label="Format (regular expression)"
                                        placeholder="Optional"
                                        :value="form.lookupValidationRegex || ''"
                                        @input="onRegexInput"
                                    />
                                </div>
                                <p v-if="lookupFieldMissing" class="GoAppSettings__problem">
                                    The saved lookup property no longer exists. Choose another before saving.
                                </p>
                                <p v-else-if="lookupFieldUnsupported" class="GoAppSettings__problem">
                                    The saved lookup property is not a text or number property. Choose another before saving.
                                </p>
                                <div class="GoAppSettings__row GoAppSettings__row--tester">
                                    <TextInput
                                        id="goAppSettingsRegexTestValue"
                                        label="Test a value"
                                        placeholder="e.g. AB1234"
                                        :value="testValue"
                                        @input="onTestValueInput"
                                    />
                                    <div class="GoAppSettings__testResult">
                                        <LabelBadge
                                            v-if="regexTestResult.invalidPattern"
                                            color="danger"
                                            size="sm"
                                            data-test-id="GoAppSettings--testResult"
                                        >
                                            Invalid format
                                        </LabelBadge>
                                        <LabelBadge
                                            v-else-if="testValue"
                                            :color="regexTestResult.matches ? 'success' : 'danger'"
                                            size="sm"
                                            data-test-id="GoAppSettings--testResult"
                                        >
                                            {{ regexTestResult.matches ? 'Matches format' : 'Doesn’t match format' }}
                                        </LabelBadge>
                                    </div>
                                </div>
                                <div class="GoAppSettings__options">
                                    <CheckboxInput
                                        name="goAppSettingsCaseInsensitive"
                                        label="Ignore uppercase and lowercase"
                                        :value="true"
                                        :model-value="form.lookupValidationCaseInsensitive"
                                        :disabled="saving"
                                        @change="form.lookupValidationCaseInsensitive = $event.target.checked"
                                    />
                                    <div class="GoAppSettings__option">
                                        <CheckboxInput
                                            name="goAppSettingsAlsoGenericSearch"
                                            label="Also allow generic search"
                                            :value="true"
                                            :model-value="form.alsoGenericSearch"
                                            :disabled="saving"
                                            @change="form.alsoGenericSearch = $event.target.checked"
                                        />
                                        <span class="GoAppSettings__choiceDescription GoAppSettings__optionHint">
                                            Employees can also search by entity name, email, or any of its properties.
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="GoAppSettings__choice"
                            :class="{ 'GoAppSettings__choice--selected': form.mode === 'generic' }"
                            data-test-id="GoAppSettings--genericMode"
                        >
                            <RadioOption
                                class="GoAppSettings__choiceHeader"
                                name="goAppLookupMode"
                                :checked="form.mode === 'generic'"
                                :disabled="saving"
                                @input="form.mode = 'generic'"
                            >
                                <span class="GoAppSettings__choiceTitle">Generic search</span>
                                <span class="GoAppSettings__choiceDescription">
                                    Search by entity name, email, or any of its properties.
                                </span>
                            </RadioOption>
                        </div>
                    </div>
                </div>

                <div class="GoAppSettings__section">
                    <div class="GoAppSettings__sectionIntro">
                        <h2 class="GoAppSettings__sectionTitle">
                            Adding entities
                        </h2>
                        <p class="GoAppSettings__sectionDescription">
                            Lets employees create a new entity when search finds no match.
                        </p>
                    </div>
                    <div class="GoAppSettings__sectionBody">
                        <div class="GoAppSettings__toggle" data-test-id="GoAppSettings--addEntityToggle">
                            <!-- Clicking the text flips the switch too, like a label would. -->
                            <span class="GoAppSettings__toggleText" @click="flip('addEntityEnabled')">
                                <span class="GoAppSettings__choiceTitle">Allow employees to add entities</span>
                                <span class="GoAppSettings__choiceDescription">
                                    {{ form.addEntityEnabled
                                        ? 'On · An “Add entity” option appears in Evident Go.'
                                        : 'Off · Employees can only search existing entities.' }}
                                </span>
                            </span>
                            <SwitchToggle
                                :disabled="saving"
                                :value="form.addEntityEnabled"
                                aria-label="Allow employees to add entities"
                                hide-label
                                @input="form.addEntityEnabled = $event"
                            />
                        </div>
                    </div>
                </div>

                <div class="GoAppSettings__section">
                    <div class="GoAppSettings__sectionIntro">
                        <h2 class="GoAppSettings__sectionTitle">
                            Email notifications
                        </h2>
                        <p class="GoAppSettings__sectionDescription">
                            Emails sent to entities added.
                        </p>
                    </div>
                    <div class="GoAppSettings__sectionBody">
                        <div class="GoAppSettings__toggle" data-test-id="GoAppSettings--emailToggle">
                            <!-- Clicking the text flips the switch too, like a label would. -->
                            <span class="GoAppSettings__toggleText" @click="flip('emailEntityOnCreation')">
                                <span class="GoAppSettings__choiceTitle">Send email notifications</span>
                                <span class="GoAppSettings__choiceDescription">
                                    {{ form.emailEntityOnCreation
                                        ? 'On · New entities get an email to submit their documents.'
                                        : 'Off · No emails are sent to new entities.' }}
                                </span>
                            </span>
                            <SwitchToggle
                                :disabled="saving"
                                :value="form.emailEntityOnCreation"
                                aria-label="Send email notifications"
                                hide-label
                                @input="form.emailEntityOnCreation = $event"
                            />
                        </div>
                    </div>
                </div>
            </section>

            <p v-if="saveError" class="GoAppSettings__saveError">
                {{ saveError }}
            </p>
            <!-- The Save tooltip carries the reason too; standing text only once there are edits to save. -->
            <p v-if="isDirty && saveBlockedReason" class="GoAppSettings__blockedReason">
                {{ saveBlockedReason }}
            </p>
        </div>
    </PageWrapper>
</template>

<style lang="scss">
    .GoAppSettings {
        &__header {
            flex-wrap: wrap;
            align-items: flex-end;
            // The page's usual header spacing leaves a wide gap above the settings card.
            padding-bottom: 0;
        }

        &__headerText {
            display: flex;
            flex: 1;
            flex-direction: column;
            gap: 2px;
        }

        &__actions {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        &__title {
            font-weight: bold;
        }

        &__description {
            font-size: var(--text-body-size, 14px);
        }

        &__form {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }

        &__card {
            display: flex;
            flex-direction: column;
            border-radius: 8px;
            background-color: var(--eidWhite);
            box-shadow: 0 1px 3px var(--eidQualityBlack-10);
        }

        // Title and description on the left, the controls spanning the rest; stacked when narrow.
        &__section {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
            position: relative;
            gap: 24px 48px;
            padding: 28px;

            // The divider stops at the card's padding rather than running edge to edge.
            & + &::before {
                content: "";
                position: absolute;
                top: 0;
                right: 28px;
                left: 28px;
                border-top: 1px solid var(--eidTechGray-25);
            }
        }

        &__sectionIntro {
            display: flex;
            flex-direction: column;
            gap: 6px;
            max-width: 320px;
        }

        &__sectionTitle {
            margin: 0;
            font-size: var(--text-section-header-size, 16px);
            font-weight: bold;
            color: var(--eidDarkBlue);
        }

        &__sectionDescription {
            margin: 0;
            font-size: var(--text-small-size, 12px);
            line-height: 1.5;
            color: var(--eidTechGray);
        }

        &__sectionBody {
            grid-column: span 2;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        &__choice {
            border: 1px solid var(--eidLightGray-50);
            border-radius: 8px;
            background-color: var(--eidWhite);

            &--selected {
                border-color: var(--eidTrustGreen);
                box-shadow: 0 0 0 0.5px var(--eidTrustGreen);
            }
        }

        &__choiceHeader {
            position: relative;
            padding: 16px 18px;

            // RadioOption hides its input with display: none, which takes it out of the tab order and
            // the accessibility tree; keep it there, just invisible, and show focus on the drawn circle.
            .RadioOption__input {
                display: block;
                position: absolute;
                width: 1px;
                height: 1px;
                opacity: 0;
            }

            .RadioOption__input:focus-visible + .RadioOption__icon {
                outline: 2px solid var(--eidTrustGreen);
                outline-offset: 2px;
            }

            .RadioOption__description {
                display: flex;
                flex-direction: column;
                gap: 2px;
            }
        }

        &__choiceTitle {
            font-size: var(--text-body-size, 14px);
            font-weight: 500;
            color: var(--eidQualityBlack);
        }

        &__choiceDescription {
            font-size: var(--text-small-size, 12px);
            font-weight: normal;
            color: var(--eidTechGray);
        }

        &__choiceBody {
            display: flex;
            flex-direction: column;
            gap: 18px;
            padding: 4px 18px 20px 46px;
        }

        &__row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 220px), 1fr));
            gap: 16px;

            &--tester {
                align-items: end;
            }
        }

        &__testResult {
            display: flex;
            align-items: center;
            min-height: 40px;
        }

        &__options {
            display: flex;
            flex-direction: column;
            gap: 12px;
            padding-top: 16px;
            border-top: 1px solid var(--eidTechGray-25);
        }

        &__option {
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        // Lines the hint up under the label, past the checkbox and its gap.
        &__optionHint {
            padding-left: calc(1.25rem + 7px);
        }

        &__toggle {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            padding: 16px 18px;
            border: 1px solid var(--eidLightGray-50);
            border-radius: 8px;

            // The size and palette the portal's other switches use (see the fulfillment toggle).
            .SwitchToggle {
                flex-shrink: 0;
                padding: 0;
                zoom: 0.9;

                &--off .SwitchToggle__slider {
                    background-color: var(--eidLightGray);
                }

                &--on .SwitchToggle__slider {
                    background-color: var(--eidTrustGreen);
                }
            }
        }

        &__toggleText {
            cursor: pointer;
            display: flex;
            flex-direction: column;
            gap: 2px;
        }

        &__disabledNotice,
        &__loadError,
        &__blockedReason {
            font-size: var(--text-body-size, 14px);
            color: var(--eidTechGray);
        }

        &__problem,
        &__saveError {
            margin: 0;
            color: var(--eidDangerRed);
        }

        &__problem {
            margin-top: -8px;
        }
    }
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import isEqual from 'lodash/isEqual';
    import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

    import LabelBadge from '@evidentid/dashboard-commons/components/LabelBadge/LabelBadge.vue';
    import RadioOption from '@evidentid/dashboard-commons/components/RadioOption/RadioOption.vue';
    import { SwitchToggle } from '@evidentid/dashboard-commons/components/SwitchToggle';
    import { useRbacStore } from '@evidentid/dashboard-commons/modules/role-base-access-control/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { GoAppConfig } from '@evidentid/tprm-portal-lib/models/go-app';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import CheckboxInput from '@/modules/dynamic-form/components/style-inputs/checkbox-input/CheckboxInput.vue';
    import EnumInput from '@/modules/dynamic-form/components/style-inputs/enum-input/EnumInput.vue';
    import TextInput from '@/modules/dynamic-form/components/style-inputs/text-input/TextInput.vue';
    import UnsavedChangesModal from '@/modules/entity-details/components/UnsavedChangesModal/UnsavedChangesModal.vue';
    import {
        compareByName,
        GO_APP_LOOKUP_TYPES,
        requiredUncollectableProperties,
    } from '@/modules/go-app-settings/propertyTypes';
    import { useGoAppSettingsStore } from '@/modules/go-app-settings/store';
    import { usePageLayoutStore } from '@/modules/page-layout/store';

    interface Option {
        value: string;
        label: string;
    }

    type LookupMode = 'custom' | 'generic';

    // The custom-property settings stay in the form while generic search is picked, so switching
    // back restores them; only what the picked mode uses is saved.
    interface GoAppSettingsForm {
        mode: LookupMode;
        lookupFieldKey: string | null;
        lookupValidationRegex: string | null;
        lookupValidationCaseInsensitive: boolean;
        alsoGenericSearch: boolean;
        addEntityEnabled: boolean;
        emailEntityOnCreation: boolean;
    }

    const DEFAULT_CONFIG: GoAppConfig = {
        lookupFieldKey: null,
        lookupValidationRegex: null,
        lookupValidationCaseInsensitive: false,
        genericSearchEnabled: false,
        addEntityEnabled: true,
        emailEntityOnCreation: false,
    };

    // Only the saved fields, as fresh copies, so configs compare and edit without touching the store.
    const copyConfig = (config: Partial<GoAppConfig>): GoAppConfig => ({
        lookupFieldKey: config.lookupFieldKey ?? null,
        lookupValidationRegex: config.lookupValidationRegex ?? null,
        lookupValidationCaseInsensitive: config.lookupValidationCaseInsensitive ?? false,
        genericSearchEnabled: config.genericSearchEnabled ?? false,
        addEntityEnabled: config.addEntityEnabled ?? true,
        emailEntityOnCreation: config.emailEntityOnCreation ?? false,
    });

    const formFromConfig = (config: GoAppConfig): GoAppSettingsForm => {
        const genericOnly = config.lookupFieldKey === null && config.genericSearchEnabled;
        return {
            mode: genericOnly ? 'generic' : 'custom',
            lookupFieldKey: config.lookupFieldKey,
            lookupValidationRegex: config.lookupValidationRegex,
            lookupValidationCaseInsensitive: config.lookupValidationCaseInsensitive,
            alsoGenericSearch: !genericOnly && config.genericSearchEnabled,
            addEntityEnabled: config.addEntityEnabled,
            emailEntityOnCreation: config.emailEntityOnCreation,
        };
    };

    const configFromForm = (form: GoAppSettingsForm): GoAppConfig => {
        const custom = form.mode === 'custom';
        return {
            lookupFieldKey: custom ? form.lookupFieldKey : null,
            lookupValidationRegex: custom ? form.lookupValidationRegex : null,
            lookupValidationCaseInsensitive: custom && form.lookupValidationCaseInsensitive,
            genericSearchEnabled: !custom || form.alsoGenericSearch,
            addEntityEnabled: form.addEntityEnabled,
            emailEntityOnCreation: form.emailEntityOnCreation,
        };
    };

    export default defineComponent({
        name: 'GoAppSettings',
        components: {
            CheckboxInput,
            EnumInput,
            LabelBadge,
            NewButton,
            PageWrapper,
            RadioOption,
            SwitchToggle,
            TextInput,
            UnsavedChangesModal,
        },
        // Navigating away with unsaved edits asks first, the way the rest of the portal does.
        beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void {
            this.confirmNavigation(next);
        },
        // Switching client pushes this same route with a new rpId, so the component is reused and
        // only this guard runs — without it those edits would be dropped with no prompt at all.
        beforeRouteUpdate(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void {
            this.confirmNavigation(next);
        },
        data() {
            return {
                pendingNavigation: null as NavigationGuardNext | null,
                form: formFromConfig(DEFAULT_CONFIG),
                testValue: '',
                // The config as last loaded or saved; Save is offered only when the form differs from it.
                baseline: copyConfig(DEFAULT_CONFIG),
            };
        },
        computed: {
            rpName(): string {
                return (this as any).$rp.current;
            },
            goAppEnabled(): boolean {
                return (this as any).$rp.currentDetails?.goAppEnabled || false;
            },
            configStatus(): { status: OperationStatus, data: GoAppConfig | null } {
                return useGoAppSettingsStore().goAppConfig[this.rpName] ||
                    { status: OperationStatus.uninitialized, data: null };
            },
            customProperties(): CustomProperty[] {
                return useDashboardStore().customProperties[this.rpName]?.list || [];
            },
            // The list is emptied while it reloads, so it only says a field was deleted once loaded.
            customPropertiesLoaded(): boolean {
                return useDashboardStore().customProperties[this.rpName]?.status === OperationStatus.success;
            },
            customPropertiesFailed(): boolean {
                return useDashboardStore().customProperties[this.rpName]?.status === OperationStatus.error;
            },
            isLoading(): boolean {
                const customPropertiesStatus = useDashboardStore().customProperties[this.rpName]?.status;
                return this.configStatus.status === OperationStatus.loading ||
                    customPropertiesStatus === OperationStatus.loading;
            },
            loadFailed(): boolean {
                return this.configStatus.status === OperationStatus.error;
            },
            lookupFieldOptions(): Option[] {
                return this.customProperties
                    .filter((property) => GO_APP_LOOKUP_TYPES.includes(property.type))
                    .sort(compareByName)
                    .map((property) => ({ value: property.key, label: property.name }));
            },
            lookupProperty(): CustomProperty | undefined {
                return this.customProperties.find((property) => property.key === this.form.lookupFieldKey);
            },
            lookupFieldMissing(): boolean {
                return this.customPropertiesLoaded && this.form.lookupFieldKey !== null && !this.lookupProperty;
            },
            lookupFieldUnsupported(): boolean {
                return Boolean(this.lookupProperty) &&
                    !GO_APP_LOOKUP_TYPES.includes((this.lookupProperty as CustomProperty).type);
            },
            config(): GoAppConfig {
                return configFromForm(this.form);
            },
            isDirty(): boolean {
                return !isEqual(this.config, this.baseline);
            },
            saveBlockedReason(): string | null {
                if (this.customPropertiesFailed) {
                    return 'Custom properties could not be loaded. Reload the page to edit these settings.';
                }
                if (!this.customPropertiesLoaded) {
                    return 'Loading custom properties…';
                }
                if (this.form.mode === 'custom') {
                    if (this.form.lookupFieldKey === null) {
                        return 'Choose a lookup property.';
                    }
                    if (this.lookupFieldMissing) {
                        return 'Choose a lookup property that still exists.';
                    }
                    if (this.lookupFieldUnsupported) {
                        return 'Choose a text or number lookup property.';
                    }
                    // The backend only compiles the pattern with Java; the app enforces it with JavaScript,
                    // so a Java-only pattern would save fine and then break every lookup.
                    if (this.regexTestResult.invalidPattern) {
                        return 'Enter a lookup format the Go App can use.';
                    }
                }
                const uncollectable = this.form.addEntityEnabled
                    ? requiredUncollectableProperties(this.customProperties, this.config.lookupFieldKey)[0]
                    : null;
                if (uncollectable) {
                    return `${uncollectable.name} is required for every entity but can't be entered in the app. `
                        + 'Make it optional or turn off adding entities.';
                }
                return null;
            },
            // The backend checks the pattern with Java's regex engine, but the Go App enforces it
            // with JavaScript's, which rejects some Java-only syntax — so test it here the way the app will.
            regexTestResult(): { invalidPattern: boolean, matches?: boolean } {
                const pattern = this.form.lookupValidationRegex;
                if (!pattern) {
                    return { invalidPattern: false, matches: true };
                }
                try {
                    return {
                        invalidPattern: false,
                        matches: new RegExp(pattern, this.form.lookupValidationCaseInsensitive ? 'i' : '').test(this.testValue),
                    };
                } catch {
                    return { invalidPattern: true };
                }
            },
            saveStatus(): { status: OperationStatus, error: any } {
                return useGoAppSettingsStore().saveStatus[this.rpName] ||
                    { status: OperationStatus.uninitialized, error: null };
            },
            saving(): boolean {
                return this.saveStatus.status === OperationStatus.loading;
            },
            // eid-insurance answers a 400 with `{ error: '<ErrorName>', message: '<details>' }`.
            saveError(): string | null {
                const { status, error } = this.saveStatus;
                if (status !== OperationStatus.error) {
                    return null;
                }
                return error?.message || 'Saving the Go App settings failed.';
            },
        },
        watch: {
            '$rp.current': {
                immediate: true,
                async handler(newVal: string, oldVal: string) {
                    // The rest of the form is loaded back by the `configStatus` watcher, which only
                    // fires on a successful load — a client with GoApp off never loads at all, and
                    // a failed one never succeeds. Without this the previous client's edits stay on
                    // screen behind the notice, and leaving the page asks to save them.
                    this.form = formFromConfig(DEFAULT_CONFIG);
                    this.baseline = copyConfig(DEFAULT_CONFIG);
                    this.testValue = '';
                    useGoAppSettingsStore().clearSaveStatus({ rpName: newVal });
                    if (newVal && newVal !== oldVal) {
                        await useRbacStore().validateRbac([ RbacFeature.configurationGoAppSettings ]);
                    }
                    this.loadSettings();
                },
            },
            // The save error belongs to the form as it was sent; editing it makes the message stale.
            'form': {
                deep: true,
                handler(): void {
                    if (this.saveStatus.status === OperationStatus.error) {
                        useGoAppSettingsStore().clearSaveStatus({ rpName: this.rpName });
                    }
                },
            },
            // The RP details (and so the flag) can arrive after the RP name.
            'goAppEnabled'(): void {
                this.loadSettings();
            },
            'configStatus': {
                immediate: true,
                handler() {
                    if (this.configStatus.status === OperationStatus.success && this.configStatus.data) {
                        this.baseline = copyConfig(this.configStatus.data);
                        this.form = formFromConfig(this.baseline);
                    }
                },
            },
        },
        created(): void {
            usePageLayoutStore().resetPageLayout();
        },
        methods: {
            // Go App settings only matter for an RP with the Go App enabled, so nothing is requested until
            // the flag is on.
            loadSettings(): void {
                if (!this.rpName || !this.goAppEnabled) {
                    return;
                }
                useGoAppSettingsStore().loadGoAppConfig({ rpName: this.rpName });
                useDashboardStore().loadCustomProperties({ rpName: this.rpName });
            },
            onLookupFieldChange(key?: string): void {
                this.form.lookupFieldKey = key || null;
            },
            onRegexInput(event: Event): void {
                this.form.lookupValidationRegex = (event.target as HTMLInputElement).value || null;
            },
            onTestValueInput(event: Event): void {
                this.testValue = (event.target as HTMLInputElement).value;
            },
            flip(setting: 'addEntityEnabled' | 'emailEntityOnCreation'): void {
                if (!this.saving) {
                    this.form[setting] = !this.form[setting];
                }
            },
            cancel(): void {
                this.form = formFromConfig(this.baseline);
                this.testValue = '';
                useGoAppSettingsStore().clearSaveStatus({ rpName: this.rpName });
            },
            confirmNavigation(next: NavigationGuardNext): void {
                if (!this.isDirty || this.saving) {
                    next();
                    return;
                }
                this.pendingNavigation = next;
            },
            onUnsavedChoice(returnToPage: boolean): void {
                const proceed = this.pendingNavigation;
                this.pendingNavigation = null;
                proceed?.(!returnToPage);
            },
            async save(): Promise<void> {
                const sent = this.config;
                await useGoAppSettingsStore().saveGoAppConfig({ rpName: this.rpName, config: sent });

                if (this.saveStatus.status === OperationStatus.success) {
                    // What the server now holds is what was sent, not whatever the form says by the
                    // time it answers. The store also reloads it; this covers the form until then.
                    this.baseline = copyConfig(sent);
                    useSnackbarStore().displaySnackbar({
                        message: 'Go App settings saved successfully.',
                        success: true,
                    });
                }
            },
        },
    });
</script>
